import { differenceInMinutes, differenceInSeconds, format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

/**
 * Formats duration between two timestamps
 * Examples:
 * - "2h 15m" (2 hours 15 minutes)
 * - "45m" (45 minutes)
 * - "8h 30m" (8 hours 30 minutes)
 *
 * @param startISO - Start timestamp (ISO 8601)
 * @param endISO - End timestamp (ISO 8601)
 * @returns Formatted duration string
 */
export function formatDuration(startISO: string, endISO: string): string {
  const start = new Date(startISO);
  const end = new Date(endISO);

  const totalSeconds = differenceInSeconds(end, start);
  const totalMinutes = differenceInMinutes(end, start);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  // Handle edge cases
  if (totalSeconds < 0) {
    return 'Invalid range';
  }

  if (hours === 0) {
    return `${minutes}m`;
  }

  return `${hours}h ${minutes}m`;
}

/**
 * Calculates the average speed based on distance and duration
 * Formula: speed = distance / duration
 *
 * @param distance - Distance in kilometers
 * @param durationMinutes - Duration in minutes
 * @returns Average speed in km/h (rounded to 1 decimal)
 */
export function calculateAverageSpeed(distance: number, durationMinutes: number): number {
  if (durationMinutes === 0) return 0;

  const durationHours = durationMinutes / 60;
  const avgSpeed = distance / durationHours;

  return Math.round(avgSpeed * 10) / 10;
}

/**
 * Format date in Europe/Brussels timezone
 * Converts UTC timestamp to CET/CEST and formats it
 *
 * @param isoDate - ISO 8601 timestamp
 * @param formatString - date-fns format string (default: 'dd/MM/yyyy HH:mm:ss')
 * @returns Formatted date in Europe/Brussels timezone
 */
export function formatInBrusselsTimezone(
  isoDate: string,
  formatString: string = 'dd/MM/yyyy HH:mm:ss'
): string {
  const utcDate = parseISO(isoDate);
  const brusselsDate = toZonedTime(utcDate, 'Europe/Brussels');
  return format(brusselsDate, formatString);
}

/**
 * Format time only in Europe/Brussels timezone
 *
 * @param isoDate - ISO 8601 timestamp
 * @returns Time formatted as HH:mm:ss in Europe/Brussels timezone
 */
export function formatTimeInBrussels(isoDate: string): string {
  return formatInBrusselsTimezone(isoDate, 'HH:mm:ss');
}

/**
 * Format date and time in Europe/Brussels timezone
 *
 * @param isoDate - ISO 8601 timestamp
 * @returns Date and time formatted as dd/MM HH:mm in Europe/Brussels timezone
 */
export function formatDateTimeInBrussels(isoDate: string): string {
  return formatInBrusselsTimezone(isoDate, 'dd/MM HH:mm');
}
